/**
 * SHOP TIME MODULE - Single Source of Truth for Timezone Operations
 * 
 * CRITICAL RULES:
 * 1. All utilities accept shopTz as a parameter - NO internal fetching
 * 2. Use TimezoneContext for frontend to get shopTz
 * 3. selectedDayLabel (YYYY-MM-DD) is the authoritative day identifier
 * 4. All timestamps stored as UTC; shop-local only for display/labels
 * 
 * DST CONTRACT:
 * - Spring forward (non-existent hour): snap-forward to next valid time
 * - Fall back (ambiguous hour): pick first occurrence (before DST ends)
 */

import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { format, parse, isValid } from 'date-fns';
import { clock } from './clock';

/**
 * Typed error for shopLocalToUtc conversion failures.
 * Thrown instead of silently returning "now" to prevent schedule corruption.
 * Critical callers (breakAdjustment, TimezoneContext) pre-validate timezone,
 * so this is a last-resort safety net.
 */
export class ShopTimeConversionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ShopTimeConversionError';
  }
}

/**
 * Convert a UTC ISO string to shop-local date label (YYYY-MM-DD)
 * 
 * @param utcIso - UTC ISO string (e.g., "2025-03-09T07:30:00.000Z")
 * @param shopTz - IANA timezone (e.g., "America/Los_Angeles")
 * @returns Date string in YYYY-MM-DD format in shop timezone
 */
export function getShopDayLabel(utcIso: string, shopTz: string): string {
  try {
    const utcDate = new Date(utcIso);
    if (!isValid(utcDate)) {
      console.warn('[shopTime] Invalid UTC date:', utcIso);
      return format(new Date(), 'yyyy-MM-dd');
    }
    const zonedDate = toZonedTime(utcDate, shopTz);
    return format(zonedDate, 'yyyy-MM-dd');
  } catch (error) {
    console.error('[shopTime] getShopDayLabel error:', error);
    return format(new Date(), 'yyyy-MM-dd');
  }
}

/**
 * Convert shop-local date + time to UTC ISO string
 * 
 * DST handling:
 * - Non-existent time (spring forward): snaps to next valid time
 * - Ambiguous time (fall back): picks first occurrence
 * 
 * @param dateStr - Date in YYYY-MM-DD format (shop-local)
 * @param timeStr - Time in HH:mm format (shop-local)
 * @param shopTz - IANA timezone
 * @returns UTC ISO string
 */
export function shopLocalToUtc(dateStr: string, timeStr: string, shopTz: string): string {
  try {
    // Parse as shop-local datetime string
    const localDateTimeStr = `${dateStr} ${timeStr}`;
    
    // fromZonedTime interprets the input as being in the specified timezone
    // and returns the equivalent UTC Date
    let utcDate = fromZonedTime(localDateTimeStr, shopTz);
    
    // DST gap detection via round-trip validation:
    // Convert UTC back to local and compare with input time
    // If they don't match, we're in a DST gap (spring forward)
    const zonedBack = toZonedTime(utcDate, shopTz);
    const [inputHours, inputMinutes] = timeStr.split(':').map(Number);
    const roundTripHours = zonedBack.getHours();
    const roundTripMinutes = zonedBack.getMinutes();
    
    // If round-trip doesn't match input, we're in a DST gap
    // Snap forward by 60 minutes (standard DST offset)
    if (inputHours !== roundTripHours || inputMinutes !== roundTripMinutes) {
      utcDate = new Date(utcDate.getTime() + 60 * 60 * 1000);
    }
    
    return utcDate.toISOString();
  } catch (error) {
    console.error('[shopTime] shopLocalToUtc error:', { dateStr, timeStr, shopTz, error });
    throw new ShopTimeConversionError(
      `Failed to convert ${dateStr} ${timeStr} in timezone ${shopTz}: ${error instanceof Error ? error.message : error}`
    );
  }
}

/**
 * Convert UTC ISO string to shop-local components
 * 
 * @param utcIso - UTC ISO string
 * @param shopTz - IANA timezone
 * @returns Object with dateStr, timeStr, and minutesSinceMidnight
 */
export function utcToShopLocal(utcIso: string, shopTz: string): {
  dateStr: string;
  timeStr: string;
  minutesSinceMidnight: number;
} {
  try {
    const utcDate = new Date(utcIso);
    if (!isValid(utcDate)) {
      throw new Error('Invalid UTC date');
    }
    
    const zonedDate = toZonedTime(utcDate, shopTz);
    const dateStr = format(zonedDate, 'yyyy-MM-dd');
    const timeStr = format(zonedDate, 'HH:mm');
    const hours = zonedDate.getHours();
    const minutes = zonedDate.getMinutes();
    
    return {
      dateStr,
      timeStr,
      minutesSinceMidnight: hours * 60 + minutes,
    };
  } catch (error) {
    console.error('[shopTime] utcToShopLocal error:', error);
    return {
      dateStr: format(new Date(), 'yyyy-MM-dd'),
      timeStr: '00:00',
      minutesSinceMidnight: 0,
    };
  }
}

/**
 * Validate that a string is a valid IANA timezone
 * 
 * @param tz - Timezone string to validate
 * @returns true if valid IANA timezone
 */
export function isValidIanaTimezone(tz: string): boolean {
  if (!tz || typeof tz !== 'string') return false;
  
  try {
    // Try to use the timezone with Intl.DateTimeFormat
    Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Get technician's hours for a specific day.
 *
 * CANONICAL SOURCE (DMC-G2):
 *   `tech.hours` jsonb is the SOLE canonical scheduling source. The
 *   per-weekday `{ open, close }` map is keyed by either numeric
 *   weekday ("0"–"6") or day-name ("Sunday"–"Saturday"); both are
 *   supported. When the resolved day has no entry — or when `hours`
 *   itself is null/empty — this function returns null. Callers MUST
 *   handle the null return as "tech not working that day."
 *
 * HISTORY (DMC-G1 + DMC-G2):
 *   Pre-DMC-G this function fell back to legacy `tech.shift_start` /
 *   `tech.shift_end` columns when the per-day hours entry was empty.
 *   DMC-G1 (migration `20260510120000_dmc_g1_backfill_shift_start_end.sql`)
 *   normalized any drifty rows by backfilling shift_start/end from the
 *   canonical `hours` envelope, leaving zero remaining drifts. DMC-G2
 *   removes the fallback so `hours` is the unambiguous SoT for
 *   scheduling. The legacy columns remain on the schema (`TechnicianForm`
 *   continues to derive them from the hours envelope on every save —
 *   they're a denormalized convenience for non-scheduling readers like
 *   FormulaDebugPanel) but are no longer consulted here.
 *
 * @param tech - Technician object with hours record. The `shift_start`
 *   and `shift_end` fields are kept on the type for back-compat with
 *   existing call sites that pass full Ticket/Technician rows; they
 *   are NOT read by this function as of DMC-G2.
 * @param boardDateStr - Date in YYYY-MM-DD format (shop-local)
 * @returns Open/close times for the resolved day, or null if not working
 */
export function getTechDayHours(
  tech: {
    // Accept the canonical (both-required) shape and the looser
    // TechnicianLike shape (numeric-or-name keys, optional open/close).
    // The body validates `open && close` before returning, so admitting
    // the looser input never yields a partial result.
    hours?: Record<number | string, { open?: string; close?: string }> | null;
    shift_start?: string | null;
    shift_end?: string | null;
  },
  boardDateStr: string
): { open: string; close: string } | null {
  // Parse the date to get day of week
  const date = parse(boardDateStr, 'yyyy-MM-dd', new Date());
  if (!isValid(date)) return null;

  const dayIndex = date.getDay(); // 0=Sunday, 1=Monday, ...
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayName = dayNames[dayIndex];

  // DMC-G2: technicians.hours is the sole canonical scheduling source.
  // Per-day hours support both numeric keys ("0"–"6") and day-name keys
  // ("Sunday"–"Saturday"). When the resolved day has no entry, return
  // null — callers handle that as "tech not working." The pre-DMC-G2
  // fallback to tech.shift_start / tech.shift_end was removed after
  // DMC-G1's backfill normalized every drifty row.
  const dayHoursByIndex = tech.hours?.[String(dayIndex)];
  const dayHoursByName = tech.hours?.[dayName];
  const dayHours = dayHoursByIndex || dayHoursByName;

  if (dayHours?.open && dayHours?.close) {
    return { open: dayHours.open, close: dayHours.close };
  }

  return null;
}

/**
 * Calculate technician's shift end as UTC Date for a specific board date
 * 
 * @param boardDateStr - Date in YYYY-MM-DD format (shop-local)
 * @param shiftEndTime - Shift end time in HH:mm format (shop-local)
 * @param shopTz - IANA timezone
 * @returns UTC Date for when the shift ends
 */
export function getTechShiftBoundaryUtc(
  boardDateStr: string,
  shiftTime: string,
  shopTz: string
): Date {
  const utcIso = shopLocalToUtc(boardDateStr, shiftTime, shopTz);
  return new Date(utcIso);
}

/**
 * Get "today" label in shop timezone
 *
 * Chronomancer R-PAST-4: routes through `clock.now()` so tests that
 * freeze time via `clock.setFixed(date)` see a stable "today" value.
 * Previously used raw `new Date()`, which ignored the clock abstraction
 * — any test asserting "today rolled over" by advancing the clock saw
 * the real wall-clock date here, producing non-deterministic results.
 *
 * @param shopTz - IANA timezone
 * @returns Today's date in YYYY-MM-DD format (shop-local)
 */
export function getTodayInShopTz(shopTz: string): string {
  return getShopDayLabel(clock.now().toISOString(), shopTz);
}

/**
 * Check if a UTC time is in the past relative to shop timezone "now"
 *
 * Chronomancer R-PAST-4: routes through `clock.nowMs()` for the same
 * test-determinism reason as `getTodayInShopTz`. The comparison
 * semantics are identical (both sides in UTC); this only changes the
 * "now" source.
 *
 * @param utcIso - UTC ISO string to check
 * @param shopTz - IANA timezone (used for context, comparison is in UTC)
 * @returns true if the time is in the past
 */
export function isTimeInPast(utcIso: string, shopTz: string): boolean {
  const targetTime = new Date(utcIso).getTime();
  const now = clock.nowMs();
  return targetTime < now;
}

/**
 * Add minutes to a UTC ISO string
 * 
 * @param utcIso - UTC ISO string
 * @param minutes - Minutes to add (can be negative)
 * @returns New UTC ISO string
 */
export function addMinutesUtc(utcIso: string, minutes: number): string {
  const date = new Date(utcIso);
  date.setTime(date.getTime() + minutes * 60 * 1000);
  return date.toISOString();
}

/**
 * Validate that a UTC instant falls on the expected shop-local date
 * Used by edge functions to validate client-sent UTC times
 * 
 * @param utcIso - UTC ISO string to validate
 * @param expectedDateStr - Expected date in YYYY-MM-DD format (shop-local)
 * @param shopTz - IANA timezone
 * @returns true if the UTC time falls on the expected shop-local date
 */
export function validateUtcOnDate(
  utcIso: string,
  expectedDateStr: string,
  shopTz: string
): boolean {
  const actualDateStr = getShopDayLabel(utcIso, shopTz);
  return actualDateStr === expectedDateStr;
}
